<!--
  Based on vue-js-toggle-button (MIT)
  Migrated from Vue 2 to Vue 3.
  Kept the original component API to avoid changing existing usages.
-->
<template>
	<label :class="className">
		<input
			type="checkbox"
			class="v-switch-input"
			:name="name"
			:checked="toggled"
			:disabled="disabled"
			@change.stop="toggle"
		/>
		<div class="v-switch-core" :style="coreStyle">
			<div class="v-switch-button" :style="buttonStyle" />
		</div>
		<template v-if="labels">
			<span v-if="toggled" class="v-switch-label v-left" :style="labelStyle">
				<slot name="checked">{{ labelChecked }}</slot>
			</span>
			<span v-else class="v-switch-label v-right" :style="labelStyle">
				<slot name="unchecked">{{ labelUnchecked }}</slot>
			</span>
		</template>
	</label>
</template>

<script>
const DEFAULT_COLOR_CHECKED = "#75c791";
const DEFAULT_COLOR_UNCHECKED = "#bfcbd9";
const DEFAULT_LABEL_CHECKED = "on";
const DEFAULT_LABEL_UNCHECKED = "off";
const DEFAULT_SWITCH_COLOR = "#fff";

function isObject(value) {
	return typeof value === "object" && value !== null;
}

function has(object, key) {
	return isObject(object) && Object.prototype.hasOwnProperty.call(object, key);
}

function get(object, key, defaultValue) {
	return has(object, key) ? object[key] : defaultValue;
}

function px(value) {
	return `${value}px`;
}

function translate3d(x, y, z = "0px") {
	return `translate3d(${x}, ${y}, ${z})`;
}

export default {
	name: "ToggleButton",
	props: {
		value: {
			type: [Boolean, Number, String],
			default: false,
		},
		modelValue: {
			type: [Boolean, Number, String],
			default: undefined,
		},
		name: {
			type: String,
		},
		disabled: {
			type: Boolean,
			default: false,
		},
		tag: {
			type: String,
		},
		sync: {
			type: [Boolean, String],
			default: false,
		},
		speed: {
			type: Number,
			default: 300,
		},
		color: {
			type: [String, Object],
		},
		switchColor: {
			type: [String, Object],
		},
		cssColors: {
			type: Boolean,
			default: false,
		},
		labels: {
			type: [Boolean, Object],
			default: false,
		},
		height: {
			type: Number,
			default: 22,
		},
		width: {
			type: Number,
			default: 50,
		},
		margin: {
			type: Number,
			default: 3,
		},
		fontSize: {
			type: Number,
		},
	},
	data() {
		return {
			toggled: !!this.resolveValue(),
		};
	},
	computed: {
		isSync() {
			return this.sync === true || this.sync === "" || this.sync === "true";
		},
		className() {
			return [
				"vue-js-switch",
				{
					toggled: this.toggled,
					disabled: this.disabled,
				},
			];
		},
		coreStyle() {
			return {
				width: px(this.width),
				height: px(this.height),
				backgroundColor: this.cssColors ? null : this.disabled ? this.colorDisabled : this.colorCurrent,
				borderRadius: px(Math.round(this.height / 2)),
			};
		},
		buttonRadius() {
			return this.height - this.margin * 2;
		},
		distance() {
			return px(this.width - this.height + this.margin);
		},
		buttonStyle() {
			const margin = px(this.margin);
			const transform = this.toggled ? translate3d(this.distance, margin) : translate3d(margin, margin);

			return {
				width: px(this.buttonRadius),
				height: px(this.buttonRadius),
				transition: `transform ${this.speed}ms`,
				transform,
				background: this.switchColor ? this.switchColorCurrent : null,
			};
		},
		labelStyle() {
			return {
				lineHeight: px(this.height),
				fontSize: this.fontSize ? px(this.fontSize) : null,
			};
		},
		colorChecked() {
			const { color } = this;
			if (!isObject(color)) {
				return color || DEFAULT_COLOR_CHECKED;
			}
			return get(color, "checked", DEFAULT_COLOR_CHECKED);
		},
		colorUnchecked() {
			return get(this.color, "unchecked", DEFAULT_COLOR_UNCHECKED);
		},
		colorDisabled() {
			return get(this.color, "disabled", this.colorCurrent);
		},
		colorCurrent() {
			return this.toggled ? this.colorChecked : this.colorUnchecked;
		},
		labelChecked() {
			return get(this.labels, "checked", DEFAULT_LABEL_CHECKED);
		},
		labelUnchecked() {
			return get(this.labels, "unchecked", DEFAULT_LABEL_UNCHECKED);
		},
		switchColorChecked() {
			return get(this.switchColor, "checked", DEFAULT_SWITCH_COLOR);
		},
		switchColorUnchecked() {
			return get(this.switchColor, "unchecked", DEFAULT_SWITCH_COLOR);
		},
		switchColorCurrent() {
			if (!isObject(this.switchColor)) {
				return this.switchColor || DEFAULT_SWITCH_COLOR;
			}
			return this.toggled ? this.switchColorChecked : this.switchColorUnchecked;
		},
	},
	watch: {
		value(value) {
			this.toggled = !!value;
		},
		modelValue(value) {
			if (value !== undefined) {
				this.toggled = !!value;
			}
		},
	},
	methods: {
		resolveValue() {
			if (this.modelValue !== undefined) {
				return this.modelValue;
			}
			return this.value;
		},
		toggle(event) {
			if (this.disabled) {
				return;
			}
			const toggled = !this.toggled;

			if (!this.isSync) {
				this.toggled = toggled;
			}

			this.$emit("input", toggled);
			this.$emit("update:modelValue", toggled);
			this.$emit("change", {
				value: toggled,
				tag: this.tag,
				srcEvent: event,
			});
		},
	},
};
</script>

<style lang="scss" scoped>
.vue-js-switch {
	display: inline-block;
	position: relative;
	vertical-align: middle;
	user-select: none;
	font-size: 10px;
	cursor: pointer;

	.v-switch-input {
		opacity: 0;
		position: absolute;
		width: 1px;
		height: 1px;
	}

	.v-switch-label {
		position: absolute;
		top: 0;
		font-weight: 600;
		color: white;
		z-index: 1;

		&.v-left {
			left: 10px;
		}

		&.v-right {
			right: 10px;
		}
	}

	.v-switch-core {
		display: block;
		position: relative;
		box-sizing: border-box;
		outline: 0;
		margin: 0;
		transition: border-color 0.3s, background-color 0.3s;
		user-select: none;

		.v-switch-button {
			display: block;
			position: absolute;
			overflow: hidden;
			top: 0;
			left: 0;
			border-radius: 100%;
			background-color: #fff;
			z-index: 2;
		}
	}

	&.disabled {
		pointer-events: none;
		opacity: 0.6;
	}
}
</style>
